//#region IMPORT
import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CUL_BASIC } from './index';
import { useSearch1, useSearch2, useSave1 } from './CUL_BASIC_QUERY';
import { useSave } from '../../../../hooks/useSaveN';
import { CUL_BASIC_Schema } from './CUL_BASIC_Schema';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import AddCompanyModal from './AddCompanyModal';
import { v4 as uuidv4 } from 'uuid';
import { Import } from 'lucide-react';
import dayjs from 'dayjs';
//#endregion
const MEMBER_INPUTS = {
  depNm1: '',
  depNm2: '',
  depNm3: '',
  depNm4: '',
  authNm: '근로자',
  tbmYn: 'N',
  approvalYn: 'N',
  position: '사업주',
  rank: '',
  name: '',
  id: '',
  tel: '',
  birth: '',
  bloodType: 'A',
  address: '',
  nation: '대한민국',
};
const toArray = (v) => (Array.isArray(v) ? v : []);

// 날짜 형식 변환 함수: "06/26/2025 00:00:00" -> "YYYY-MM-DD"
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';

  // "MM/DD/YYYY HH:mm:ss" 형식 파싱
  const match = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (match) {
    const [, month, day, year] = match;
    return `${year}-${month}-${day}`;
  }

  // 이미 "YYYY-MM-DD" 형식이면 그대로 반환
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
    return dateStr;
  }

  // dayjs로 파싱 시도
  const parsed = dayjs(dateStr);
  if (parsed.isValid()) {
    return parsed.format('YYYY-MM-DD');
  }

  return '';
};

/** API paymentDay(1–31) 또는 구 paymentDate에서 매월 결제일 */
const ResolvePaymentDayForCompany = (src) => {
  if (!src || typeof src !== 'object') return '';
  const dayRaw =
    src.paymentDay ??
    src.PaymentDay ??
    src.payment_day ??
    src.PAYMENT_DAY;
  if (
    dayRaw !== undefined &&
    dayRaw !== null &&
    String(dayRaw).trim() !== ''
  ) {
    const n = Math.min(31, Math.max(1, Number(String(dayRaw).trim())));
    if (Number.isFinite(n)) return n;
  }
  const legacy =
    src.paymentDate ?? src.PaymentDate ?? src.payment_date ?? src.PAYMENT_DATE;
  if (legacy) {
    const parsed = dayjs(legacy);
    if (parsed.isValid()) return parsed.date();
  }
  return '';
};

/** Search1 companyDetails → 상세(recentActivityLogs)와 맞춘 최근 활동 한 줄 요약 */
const BuildRecentActivitySummary = (company) => {
  const direct =
    company.recentActivitySummary ??
    company.RecentActivitySummary ??
    company.lastActivitySummary ??
    company.LastActivitySummary ??
    '';
  if (String(direct).trim()) return String(direct).trim();

  const logs = toArray(company.recentActivityLogs);
  const first = logs[0];
  if (first) {
    const text =
      first.activityContent ||
      first.ActivityContent ||
      first.activity ||
      first.Activity ||
      '';
    const when =
      first.actionDt || first.ActionDt || first.time || first.Time || '';
    const t = String(text || '').trim();
    const w = String(when || '').trim();
    if (t && w) return `${t} · ${w}`;
    if (t) return t;
    if (w) return w;
  }

  const text =
    company.lastActivityContent ??
    company.LastActivityContent ??
    company.recentActivityContent ??
    '';
  const when =
    company.lastActivityDate ??
    company.LastActivityDate ??
    company.lastActivityDt ??
    company.LastActivityDt ??
    '';
  const t = String(text || '').trim();
  const w = String(when || '').trim();
  if (t && w) return `${t} · ${w}`;
  return t || w || '';
};

function CUL_BASICF() {
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const dispatch = useDispatch();

  // 모달 상태
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editCompanyData, setEditCompanyData] = useState(null);
  const modalSaveRowsRef = useRef([]);
  const [memInputs, setMemInputs] = useState(MEMBER_INPUTS);
  const [contracts, setContracts] = useState({
    totalContracts: 0,
    totalCompanyChangeRate: 0,
    expiringSoon: 0,
    expiringChangeRate: 0,
    riskyAccounts: 0,
    riskAccountChangeRate: 0,
    averageRenewalRate: 0,
    averageRenewalChangeRate: 0,
    contractList: [],
  });

  const { refetch: Search1 } = useSearch1({
    onSuccess: async (rows) => {
      const first = toArray(rows)?.[0] ?? {};
      // companyDetails를 contractList 형식으로 변환
      const companyDetails = toArray(first.companyDetails ?? []);
      const contractList = companyDetails.map((company) => {
        // status에 따라 renewalRate 추정
        let renewalRate = 0;
        if (company.status === '양호') {
          renewalRate = 70; // 양호는 70% 이상으로 간주
        } else if (company.status === '주의') {
          renewalRate = 50; // 주의는 30-70% 사이로 간주
        } else {
          renewalRate = 20; // 기타는 30% 미만으로 간주
        }

        return {
          coCd: company.coCd || company.CoCd || '',
          companyName: company.companyName || company.CompanyName || '',
          companyId:
            company.representativeId ||
            company.RepresentativeId ||
            company.companyId ||
            company.CompanyId ||
            '',
          representativeName:
            company.representativeName ||
            company.RepresentativeName ||
            '',
          contractStartDate:
            company.contractStartDate || company.ContractStartDate || '',
          contractEndDate: company.contractEndDate || '',
          remainingDays: company.remainingDays || 0,
          activeUsers: company.activeUserCount || 0,
          usageRatePercent: company.usageRatePercent || 0,
          renewalRate: renewalRate,
          status: company.status || '',
          address1: company.address1 || '',
          address2: company.address2 || '',
          monthlyContractFee:
            company.monthlyContractFee ??
            company.MonthlyContractFee ??
            '',
          paymentDay: ResolvePaymentDayForCompany(company),
          recentActivitySummary: BuildRecentActivitySummary(company),
        };
      });

      // 새로운 데이터 구조에 맞춰 파싱
      const totalCompanyCount = first.totalCompanyCount ?? {};
      const expiringContractCount = first.expiringContractCount ?? {};
      const riskAccountCount = first.riskAccountCount ?? {};
      const averageRenewalRate = first.averageRenewalRate ?? {};

      setContracts({
        totalContracts: Number(totalCompanyCount.currentCnt ?? 0),
        totalCompanyChangeRate: Number(totalCompanyCount.changeRate ?? 0),
        expiringSoon: Number(expiringContractCount.currentExpire ?? 0),
        expiringChangeRate: Number(expiringContractCount.changeRate ?? 0),
        riskyAccounts: Number(riskAccountCount.currentRisk ?? 0),
        riskAccountChangeRate: Number(riskAccountCount.changeRate ?? 0),
        averageRenewalRate: Number(averageRenewalRate.currentRate ?? 0) * 100, // 0.5 -> 50%로 변환
        averageRenewalChangeRate: Number(averageRenewalRate.changeRate ?? 0),
        contractList: contractList,
      });
    },
  });
  // Search2 결과를 저장할 state
  const [companyDetailData, setCompanyDetailData] = useState(null);

  const { run: Search2 } = useSearch2({
    onSuccess: async (rows) => {
      const first = toArray(rows)?.[0] ?? {};

      // functionalSmartSummaryList가 직접 rows에 있는 경우 처리
      let detailData = first;
      if (Array.isArray(rows) && rows.length > 0) {
        // rows의 첫 번째 항목이 객체가 아니고 배열인 경우
        if (Array.isArray(rows[0])) {
          detailData = { functionalSmartSummaryList: rows[0] };
        }
        // rows 자체가 우리가 찾는 데이터 배열인 경우
        else if (rows[0] && rows[0].categoryName) {
          detailData = { functionalSmartSummaryList: rows };
        }
        // first 객체에 functionalSmartSummaryList가 없는 경우, rows 전체를 확인
        else if (!first.functionalSmartSummaryList && !first.smartSummaryList) {
          // rows에서 배열 필드를 찾기
          for (const item of rows) {
            if (item && typeof item === 'object') {
              for (const key in item) {
                if (Array.isArray(item[key]) && item[key].length > 0) {
                  const firstItem = item[key][0];
                  if (
                    firstItem &&
                    (firstItem.categoryName || firstItem.subCategoryName)
                  ) {
                    detailData = {
                      ...item,
                      functionalSmartSummaryList: item[key],
                    };
                    break;
                  }
                }
              }
            }
          }
        }
      }

      setCompanyDetailData(detailData);
    },
  });

  useEffect(() => {
    // userInfo가 아직 없을 때는 SP 호출이 의미 없을 수 있어, 이때는 period만 유지
    if (!userInfo?.userid) return;
    Search1();
  }, [userInfo?.userid]);

  const handleExport = () => {
    // TODO: 보고서 내보내기 기능 구현
  };

  // 회사 상세 보기 핸들러 - Search2 호출
  const handleViewDetail = (companyData) => {
    if (!companyData) return;
    // companyId 또는 CoCd를 사용하여 Search2 호출
    const coCd = companyData.coCd || '';
    if (coCd) {
      setCompanyDetailData(null); // 이전 데이터 초기화
      Search2({ CoCd: coCd });
    }
  };

  // Save1 로직 (COM_BASICF.js와 동일한 구조)
  const { runAsync: Save1Async } = useSave1();
  const { run: Save1 } = useSave({
    menuId: 'Company',
    requiredLabel: '회사명',
    schema: CUL_BASIC_Schema,
    getChangedRows: () => {
      // 모달에서 전달받은 rows를 그대로 사용
      return (
        modalSaveRowsRef.current?.map((row) => ({
          ...row,
          no: row.no || uuidv4(),
          newYn: 'Y',
          address: memInputs.address,
          approvalYn: memInputs.approvalYn,
          authNm: memInputs.authNm,
          birth: memInputs.birth,
          bloodType: memInputs.bloodType,
          depNm1: memInputs.depNm1,
          depNm2: memInputs.depNm2,
          depNm3: memInputs.depNm3,
          depNm4: memInputs.depNm4,
          name: memInputs.name,
          nation: memInputs.nation,
          position: memInputs.position || '사업주',
          rank: memInputs.rank,
          tbmYn: memInputs.tbmYn,
          tel: memInputs.tel,
          id: memInputs.id,
        })) ?? []
      );
    },
    getDeletedRows: () => [],
    requestSave: (rows) => Save1Async(rows),
    requestDelete: () => Promise.resolve([]),
    refresh: async () => {
      // 저장 후 목록 새로고침
      await Search1();
    },
    reset: () => {
      // 리셋 로직
      modalSaveRowsRef.current = [];
    },
  });

  const handleAddContract = () => {
    setEditMode(false);
    setEditCompanyData(null);
    setModalOpen(true);
  };

  const handleEditContract = (companyData, detailData) => {
    setEditMode(true);
    setEditCompanyData({ companyData, detailData });

    // modalSaveRowsRef에 companyData 정보 설정
    if (companyData) {
      // companyData를 AddCompanyModal의 initialInputs 구조로 변환
      // companyData의 Id 값 확인 (id 또는 Id 필드)
      const companyDataId = companyData.id || companyData.Id || '';

      const rowData = {
        no: companyData.no || companyData.coCd || '',
        coCd: companyData.coCd || '',
        coNm: companyData.companyName || '',
        coId: companyData.companyId || '',
        name: '',
        // 주소: address1에 주소, address2에 상세주소
        address1: companyData.address1 || companyData.address || '',
        address2: companyData.address2 || '',
        contractStartDate: '',
        contractEndDate: '',
        contractMemberCount: companyData.activeUsers || 0,
        monthlyContractFee: companyData.monthlyContractFee ?? '',
        paymentDay: '',
        // 대표자 Id: companyData의 Id 값
        id: companyDataId,
        // 기존 companyData의 모든 필드도 유지
        ...companyData,
      };

      // detailData에서 추가 정보가 있으면 병합
      if (detailData?.companyInfo) {
        const companyInfo = detailData.companyInfo;
        rowData.coNm = companyInfo.companyName || rowData.coNm;
        rowData.coId = companyInfo.id || rowData.coId;
        // 주소: address1에 주소, address2에 상세주소
        rowData.address1 =
          companyInfo.address1 || companyInfo.address || rowData.address1;
        rowData.address2 = companyInfo.address2 || rowData.address2;
        rowData.contractMemberCount =
          companyInfo.totalWorkerCount || rowData.contractMemberCount;
        rowData.name = companyInfo.name || rowData.name;
        rowData.monthlyContractFee =
          companyInfo.monthlyContractFee ??
          companyData.monthlyContractFee ??
          rowData.monthlyContractFee;
        rowData.paymentDay =
          ResolvePaymentDayForCompany({
            paymentDay:
              companyInfo.paymentDay ??
              companyData.paymentDay ??
              companyInfo.PaymentDay ??
              companyData.PaymentDay,
            paymentDate:
              companyInfo.paymentDate || companyData.paymentDate || '',
          }) || '';
        // 대표자 Id: companyInfo.id가 있으면 우선, 없으면 companyData의 Id 유지
        rowData.id = companyInfo.id || companyDataId || rowData.id;

        // 날짜 형식 변환 (companyInfo에서 우선)
        rowData.contractStartDate = formatDateForInput(
          companyInfo.contractStartDate || companyData.contractStartDate || ''
        );
        rowData.contractEndDate = formatDateForInput(
          companyInfo.contractEndDate || companyData.contractEndDate || ''
        );
      } else {
        // detailData가 없으면 companyData에서 직접 변환
        rowData.contractStartDate = formatDateForInput(
          companyData.contractStartDate || ''
        );
        rowData.contractEndDate = formatDateForInput(
          companyData.contractEndDate || ''
        );
        rowData.paymentDay =
          ResolvePaymentDayForCompany(companyData) || '';
        rowData.monthlyContractFee =
          companyData.monthlyContractFee ?? rowData.monthlyContractFee;
      }

      modalSaveRowsRef.current = [rowData];
    } else {
      modalSaveRowsRef.current = [];
    }

    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditMode(false);
    setEditCompanyData(null);
    modalSaveRowsRef.current = [];
  };

  const handleModalSave = async (rows) => {
    // rows를 ref에 저장하고 Save1 실행
    modalSaveRowsRef.current = rows;
    await Save1();
    setModalOpen(false);
    setEditMode(false);
    setEditCompanyData(null);
  };

  return (
    <div className="wrap">
      <CUL_BASIC
        contracts={contracts}
        filters={{}}
        setFilters={() => {}}
        onExport={handleExport}
        onAddContract={handleAddContract}
        onViewDetail={handleViewDetail}
        companyDetailData={companyDetailData}
        onEditContract={handleEditContract}
      />
      <AddCompanyModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        userInfo={userInfo}
        setMemInputs={setMemInputs}
        memInputs={memInputs}
        editMode={editMode}
        editCompanyData={editCompanyData}
      />
    </div>
  );
}

export default CUL_BASICF;
